import { AlertResponse, ThingEventSnapshot } from '@eagle/core-data-types';
import { ThingEvent } from '@eagle/data-function-types';
import { Operator } from '@jexop/core';
import { Theme } from '@mui/material';
import { isString } from 'lodash';
import { getEventDictionary } from '../components';
import { EventDefinition } from '../notable-events';
import { getEventLabelWithFlag, getFeatureLabelWithFlag } from '../util';
import { featureActivityIsStartedOperator, latestEventOperator } from './data-feature';
import { translate } from './translate';

export const boolMacros: Record<string, Operator<boolean>> = {
  /**
   * Macro for consistently calculating if the thing has power on.
   * @example
   * { "op": "macro:is-power-on" }
   *
   * @returns A boolean indicating power on.
   */
  ['macro:is-power-on']: (_, context) =>
    featureActivityIsStartedOperator({
      feature: 'tracking',
      startEventTypeId: 'power-on',
      finishEventTypeId: 'power-off',
    }, context),

  /**
   * Macro for consistently calculating if the thing is moving.
   * @example
   * { "op": "macro:is-moving" }
   *
   * @returns A boolean indicating moving.
   */
  ['macro:is-moving']: (_, context) => {
    const result = latestEventOperator({
      feature: 'tracking',
      havingPath: 'data.moving',
      path: 'data.moving',
    }, context);
    return result === null ? null : !!result;
  },
};

export const stringMacros: Record<string, Operator<string>> = {
  /**
   * Macro for fetching the alert event translation label
   * @example
   * { "op": "macro:alert-event-description" }
   *
   * @returns A string of the alerts event description.
   */
  ['macro:alert-event-description']: (_, context: AlertResponse) => {
    const translation = `common:event-descriptions-v2.${context.featureTypeId}.${context.alertTypeId}.label`;
    const replace = { features: translate({ translation: `common:features.${context.feature}` }) };
    return translate({ translation, replace }, context);
  },
  /**
   * Macro for fetching the events translation label
   * @example
   * { "op": "macro:event-description" }
   *
   * @returns A string of the event description.
   */
  ['macro:event-description']: (props, context: ThingEvent) => {
    const lastEvent = props.lastEvent as ThingEvent ?? context;
    if (!lastEvent.featureTypeId || !lastEvent.eventTypeId) return null;
    const translation = `common:event-descriptions-v2.${lastEvent.featureTypeId}.${lastEvent.eventTypeId}.label`;
    return translate({ translation }, lastEvent);
  },

  ['macro:event-color']: (props) => {
    const event = props.event as ThingEventSnapshot;
    const theme = props.theme as Theme;
    const definitions = props.definitions as EventDefinition[];
    if (!event.featureTypeId || !event.eventTypeId) return theme.alerts.default;
    const dictionary = getEventDictionary(event, theme, definitions);
    return dictionary?.styling.backgroundColor ?? theme.alerts.default;
  },

  ['macro:feature-label']: ({ feature, flagEnabled }) => {
    const v3Enabled = !!flagEnabled;
    if (!isString(feature)) {
      return null;
    }
    return getFeatureLabelWithFlag(feature, v3Enabled);
  },

  ['macro:event-label']: ({ event, flagEnabled }) => {
    const { featureTypeId, eventTypeId, feature } = event as ThingEventSnapshot;
    const v3Enabled = !!flagEnabled;
    const featureType = isString(featureTypeId) ? featureTypeId : '';
    const eventType = isString(eventTypeId) ? eventTypeId : '';
    const featureId = isString(feature) ? feature : '';
    return getEventLabelWithFlag(featureType, eventType, featureId, v3Enabled);
  },

};

